import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { MessageSquareReply, Flame, Clock, CheckSquare, UserPlus, Mail, MousePointerClick, Eye, CalendarCheck, ChevronRight, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, StageBadge, ScoreChip } from "@/components/Layout";
import ComposeDialog from "@/components/ComposeDialog";
import { api, errMsg, STAGES, nextStage, fullName, ago } from "@/lib/api";

function Stat({ label, value, icon: Icon, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid={testid}>
      <div className="flex items-center justify-between label-xs"><span>{label}</span><Icon size={14} /></div>
      <div className="text-3xl font-extrabold tracking-tight mt-2">{value ?? "—"}</div>
    </div>
  );
}

function ActionGroup({ title, icon: Icon, items, tone, render, testid }) {
  if (!items?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={testid}>
      <div className={`px-4 py-2.5 flex items-center gap-2 text-sm font-semibold border-b ${tone}`}><Icon size={15} />{title}<span className="ml-auto font-mono text-xs">{items.length}</span></div>
      <div className="divide-y divide-slate-100">{items.slice(0, 6).map(render)}</div>
    </div>
  );
}

const Row = ({ to, title, sub, right, testid }) => (
  <Link to={to} data-testid={testid} className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 transition-colors">
    <div className="min-w-0 flex-1"><div className="text-sm font-medium truncate">{title}</div><div className="text-xs text-slate-500 truncate">{sub}</div></div>
    {right}<ChevronRight size={14} className="text-slate-300" />
  </Link>
);

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [mine, setMine] = useState(false);
  const [compose, setCompose] = useState(null);
  const [campaign, setCampaign] = useState("all");
  const [camps, setCamps] = useState([]);
  const [over, setOver] = useState(null);
  const load = () => api.get("/dashboard", { params: { mine, campaign: campaign === "all" ? "" : campaign } }).then((r) => setData(r.data));
  useEffect(() => { load(); }, [mine, campaign]); // eslint-disable-line
  useEffect(() => { api.get("/campaigns").then((r) => setCamps(r.data)); }, []);
  const runAutomation = async () => {
    try { const { data: r } = await api.post("/automation/run"); toast.success(`Automation: ${r.sent} sent, ${r.skipped} skipped, ${r.completed} completed`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const moveStage = async (lead, stage) => {
    if (lead.stage === stage) return;
    setData((d) => {
      const p = { ...d.pipeline };
      p[lead.stage] = p[lead.stage].filter((x) => x.id !== lead.id);
      p[stage] = [{ ...lead, stage }, ...p[stage]];
      return { ...d, pipeline: p };
    });
    try { await api.post(`/leads/${lead.id}/stage`, { stage }); toast.success(`${lead.company_name} → ${STAGES.find((x) => x.key === stage).label}`); }
    catch (e) { toast.error(errMsg(e)); }
    load();
  };
  const onDrop = (e, stage) => {
    e.preventDefault(); setOver(null);
    const id = e.dataTransfer.getData("text/plain");
    const lead = Object.values(data?.pipeline || {}).flat().find((l) => l.id === id);
    if (lead) moveStage(lead, stage);
  };
  const campName = (id) => camps.find((c) => c.id === id)?.name;
  const s = data?.stats; const a = data?.actions;
  const totalActions = a ? a.replies.length + a.hot.length + a.followups_due.length + a.tasks.length + a.not_contacted.length : 0;

  return (
    <div>
      <PageHeader eyebrow={new Date().toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" })} title="Sales Dashboard">
        <Select value={campaign} onValueChange={setCampaign}><SelectTrigger className="w-60" data-testid="dashboard-campaign-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All campaigns</SelectItem>{camps.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select>
        <label className="flex items-center gap-2 text-sm text-slate-600 mr-2"><Switch checked={mine} onCheckedChange={setMine} data-testid="dashboard-mine-toggle" />My leads only</label>
        <Button variant="outline" onClick={runAutomation} data-testid="run-automation-btn"><Play size={14} className="mr-1" />Run automation</Button>
      </PageHeader>
      <div className="p-6 lg:p-10 space-y-8">
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3 fade-up">
          <Stat label="Total leads" value={s?.total_leads} icon={UserPlus} testid="stat-total-leads" />
          <Stat label="Emails 7d" value={s?.emails_7d} icon={Mail} testid="stat-emails" />
          <Stat label="Opens 7d" value={s?.opens_7d} icon={Eye} testid="stat-opens" />
          <Stat label="Clicks 7d" value={s?.clicks_7d} icon={MousePointerClick} testid="stat-clicks" />
          <Stat label="Page visits 7d" value={s?.visits_7d} icon={Eye} testid="stat-visits" />
          <Stat label="Replies 7d" value={s?.replies_7d} icon={MessageSquareReply} testid="stat-replies" />
          <Stat label="Booked" value={s?.bookings} icon={CalendarCheck} testid="stat-bookings" />
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
          <section className="xl:col-span-4 space-y-3" data-testid="action-queue">
            <div className="flex items-baseline justify-between"><h2 className="text-lg font-bold">Action queue</h2><span className="label-xs">{totalActions} items</span></div>
            {a && totalActions === 0 && <div className="bg-white border border-dashed rounded-xl p-6 text-sm text-slate-500">All caught up. Nice work.</div>}
            <ActionGroup title="Replies to handle" icon={MessageSquareReply} tone="bg-red-50 text-red-700" items={a?.replies} testid="actions-replies"
              render={(r) => <Row key={r.id} to="/inbox" testid={`action-reply-${r.id}`} title={`${r.lead_name} · ${r.company_name}`} sub={r.classification?.summary || r.subject}
                right={<span className="text-[10px] uppercase font-semibold bg-red-100 text-red-700 rounded px-1.5 py-0.5">{r.classification?.category?.replace("_", " ")}</span>} />} />
            <ActionGroup title="Tasks due" icon={CheckSquare} tone="bg-slate-50 text-slate-700" items={a?.tasks} testid="actions-tasks"
              render={(t) => <Row key={t.id} to={t.lead_id ? `/leads/${t.lead_id}` : "/tasks"} testid={`action-task-${t.id}`} title={t.title} sub={`Due ${ago(t.due_at)}`} />} />
            <ActionGroup title="Hot leads — engaged, no reply" icon={Flame} tone="bg-orange-50 text-orange-700" items={a?.hot} testid="actions-hot"
              render={(l) => <Row key={l.id} to={`/leads/${l.id}`} testid={`action-hot-${l.id}`} title={`${fullName(l)} · ${l.company_name}`} sub={`${l.stats?.clicks || 0} clicks · ${l.stats?.visits || 0} visits`} right={<ScoreChip score={l.score} />} />} />
            <ActionGroup title="Follow-ups due" icon={Clock} tone="bg-amber-50 text-amber-800" items={a?.followups_due} testid="actions-followups"
              render={(l) => (
                <div key={l.id} className="flex items-center gap-3 px-4 py-2.5">
                  <Link to={`/leads/${l.id}`} className="min-w-0 flex-1"><div className="text-sm font-medium truncate">{fullName(l)} · {l.company_name}</div><div className="text-xs text-slate-500">Last contact {ago(l.last_contacted_at)}</div></Link>
                  <Button size="sm" variant="outline" className="h-7" onClick={() => setCompose(l)} data-testid={`followup-send-${l.id}`}>Send next</Button>
                </div>)} />
            <ActionGroup title="Not yet contacted" icon={UserPlus} tone="bg-blue-50 text-blue-700" items={a?.not_contacted} testid="actions-new"
              render={(l) => (
                <div key={l.id} className="flex items-center gap-3 px-4 py-2.5">
                  <Link to={`/leads/${l.id}`} className="min-w-0 flex-1"><div className="text-sm font-medium truncate">{fullName(l)} · {l.company_name}</div><div className="text-xs text-slate-500">{l.email}</div></Link>
                  <Button size="sm" className="h-7" onClick={() => setCompose(l)} data-testid={`cold-send-${l.id}`}>{l.stage === "cold_email" ? "Cold email" : "Email"}</Button>
                </div>)} />
          </section>

          <section className="xl:col-span-8" data-testid="pipeline">
            <div className="flex items-baseline justify-between mb-3"><h2 className="text-lg font-bold">Pipeline</h2><span className="label-xs">Drag cards between stages</span></div>
            <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-5 gap-3">
              {STAGES.map((st) => (
                <div key={st.key} data-testid={`pipeline-col-${st.key}`}
                  onDragOver={(e) => { e.preventDefault(); setOver(st.key); }} onDragLeave={() => setOver(null)} onDrop={(e) => onDrop(e, st.key)}
                  className={`border rounded-xl p-2.5 min-h-[420px] transition-colors ${over === st.key ? "bg-blue-50 border-blue-400 border-dashed" : "bg-slate-100/70 border-slate-200"}`}>
                  <div className="flex items-center justify-between px-1.5 pb-2.5"><StageBadge stage={st.key} /><span className="font-mono text-xs text-slate-500">{s?.stage_counts?.[st.key] ?? 0}</span></div>
                  <div className="space-y-2">
                    {(data?.pipeline?.[st.key] || []).map((l) => (
                      <div key={l.id} draggable onDragStart={(e) => { e.dataTransfer.setData("text/plain", l.id); e.dataTransfer.effectAllowed = "move"; }}
                        className="bg-white border border-slate-200 rounded-lg p-3 card-lift cursor-grab active:cursor-grabbing active:opacity-60" data-testid={`pipeline-card-${l.id}`}>
                        <Link to={`/leads/${l.id}`} className="block" draggable={false}>
                          <div className="flex items-start justify-between gap-2"><div className="text-sm font-semibold leading-tight">{l.company_name}</div><ScoreChip score={l.score} /></div>
                          <div className="text-xs text-slate-500 mt-0.5">{fullName(l)}</div>
                          {l.campaign_id && <div className="text-[10px] text-blue-700 mt-1 truncate">{campName(l.campaign_id)}</div>}
                          <div className="flex flex-wrap gap-1 mt-2 text-[10px]">
                            {l.replied && <span className="bg-red-100 text-red-700 rounded px-1.5">replied</span>}
                            {l.outcome && <span className="bg-emerald-100 text-emerald-700 rounded px-1.5">{l.outcome.replace("_", " ")}</span>}
                            <span className="text-slate-400">contacted {ago(l.last_contacted_at)}</span>
                          </div>
                        </Link>
                        <div className="flex gap-1 mt-2.5">
                          <Button size="sm" variant="outline" className="h-7 text-xs flex-1" onClick={() => setCompose(l)} data-testid={`pipeline-email-${l.id}`}>Email</Button>
                          {st.key !== "close_lead" && <Button size="sm" variant="ghost" className="h-7 text-xs" data-testid={`pipeline-advance-${l.id}`}
                            onClick={() => moveStage(l, nextStage(st.key))}>Advance →</Button>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
      <ComposeDialog lead={compose} open={!!compose} onOpenChange={(o) => !o && setCompose(null)} onSent={load} />
    </div>
  );
}
